import { useState } from 'react';
import { api, ApiError } from '../api/index.js';
import { BUILD_INFO } from '../BUILD_INFO.js';

export default function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit = username.trim().length > 0 && password.length > 0 && !loading;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    try {
      const { user } = await api.login(username.trim(), password);
      onLogin?.(user);
    } catch (err) {
      const msg =
        err instanceof ApiError && err.status === 401
          ? 'Invalid username or password.'
          : err?.message || 'Sign in failed. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  const year = new Date().getUTCFullYear();

  return (
    <div className="flex w-full flex-1 items-center justify-center bg-canvas px-4 py-10 text-ink">
      <div className="w-full max-w-[448px]">
        <form
          onSubmit={handleSubmit}
          className="rounded-[28px] border border-line bg-white p-8 shadow-lg"
          noValidate
        >
          {/* Logo */}
          <div className="flex justify-center">
            <div className="rounded-2xl border border-line bg-canvas p-3">
              <img
                src="/runtime-assets/vendor/logos/logo.svg"
                alt="TMS"
                className="h-24 w-24 object-contain"
              />
            </div>
          </div>

          {/* Project badge */}
          <div className="mt-6 flex justify-center">
            <span className="pill">
              <i className="fa-solid fa-shield-halved text-accent" aria-hidden="true" />
              TMS ADMS
            </span>
          </div>

          {/* Subtitle */}
          <h1 className="mt-3 text-center text-xl font-semibold tracking-tight text-ink">
            Tactical Mesh Service — Coordinator
          </h1>
          <p className="mt-1 text-center text-sm text-mist">
            Sign in to continue to the control plane.
          </p>

          {/* Error block */}
          {error && (
            <div
              role="alert"
              className="mt-6 rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700"
            >
              <div className="flex items-start gap-2">
                <i
                  className="fa-solid fa-circle-exclamation mt-0.5 text-rose-500"
                  aria-hidden="true"
                />
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Username */}
          <div className="mt-6">
            <label htmlFor="login-username" className="field-label">
              Username
            </label>
            <input
              id="login-username"
              name="username"
              type="text"
              autoFocus
              autoComplete="username"
              spellCheck="false"
              placeholder="Enter your username"
              className="field"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Password */}
          <div className="mt-4">
            <label htmlFor="login-password" className="field-label">
              Password
            </label>
            <input
              id="login-password"
              name="password"
              type="password"
              autoComplete="current-password"
              placeholder="Enter your password"
              className="field"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Submit */}
          <button type="submit" disabled={!canSubmit} className="btn-primary mt-6 w-full">
            {loading ? (
              <>
                <span className="loader-spinner" aria-hidden="true" />
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-right-to-bracket" aria-hidden="true" />
                <span>Sign In</span>
              </>
            )}
          </button>

          {/* In-card footer: copyright + build */}
          <div className="mt-6 flex items-center justify-between border-t border-line pt-4 text-[11px] text-mist">
            <span className="inline-flex items-center gap-1.5">
              <i className="fa-regular fa-copyright text-mist/70" aria-hidden="true" />
              {year} MicroGIS&trade;
            </span>
            <span className="inline-flex items-center gap-1.5 font-mono">
              <i className="fa-solid fa-code-branch text-mist/70" aria-hidden="true" />
              Build v{BUILD_INFO.version}
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}
